import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '@/firebase';
import ClientLayout from '@/components/layout/ClientLayout';

interface ParkingApplication {
  id: string;
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  applicationType: 'chassis' | 'individual';
  period: 1 | 2 | 3;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: any;
  updatedAt: any;
  termsAgreed: boolean;
  additionalRequest?: string;
  periodId?: string; // 신청 차수 ID
}

interface ParkingPeriod {
  id: string;
  sequence: number;
  applicationStartDate: any;
  applicationEndDate: any;
  lotteryDate: any;
  usageStartDate: any;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

const ParkingDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [application, setApplication] = useState<ParkingApplication | null>(null);
  const [currentPeriod, setCurrentPeriod] = useState<ParkingPeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    const fetchData = async () => {
      try {
        // 현재 활성화된 신청 차수 조회
        const periodsQuery = query(
          collection(db, 'parking_periods'),
          where('isActive', '==', true),
          orderBy('sequence', 'desc')
        );
        const periodsSnapshot = await getDocs(periodsQuery);
        
        if (!periodsSnapshot.empty) {
          const periodDoc = periodsSnapshot.docs[0];
          setCurrentPeriod({ id: periodDoc.id, ...periodDoc.data() } as ParkingPeriod);
        }

        // 사용자 신청 내역 조회
        const applicationsQuery = query(
          collection(db, 'parking_applications'),
          where('uid', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        
        if (!applicationsSnapshot.empty) {
          const doc = applicationsSnapshot.docs[0];
          setApplication({ id: doc.id, ...doc.data() } as ParkingApplication);
        } else {
          // 신청 내역이 없으면 메인 페이지로 이동
          navigate('/parking');
        }
      } catch (error) {
        console.error('데이터 조회 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '신청 대기중';
      case 'approved':
        return '신청 승인';
      case 'rejected':
        return '신청 거절';
      case 'cancelled':
        return '신청 취소';
      default:
        return '알 수 없음';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getApplicationTypeText = (type: string) => {
    return type === 'chassis' ? '섀시업체 신청' : '개별화물차 신청';
  };

  const isApplicationPeriodActive = () => {
    if (!currentPeriod) return false;
    
    const now = new Date();
    const startDate = currentPeriod.applicationStartDate?.toDate?.();
    const endDate = currentPeriod.applicationEndDate?.toDate?.();
    
    return startDate && endDate && now >= startDate && now <= endDate;
  };

  const canModifyOrCancel = () => {
    if (!application || !currentPeriod) return false;
    
    // 현재 신청이 현재 활성화된 차수에 속하는지 확인
    const isCurrentPeriodApplication = application.periodId === currentPeriod.id;
    
    // 신청 기간 내이고 현재 차수의 신청이며 cancelled가 아닌 경우만 수정/취소 가능
    return isApplicationPeriodActive() && 
           isCurrentPeriodApplication && 
           application.status !== 'cancelled';
  };

  const handleCancel = async () => {
    if (!application || cancelling) return;

    setCancelling(true);
    try {
      await updateDoc(doc(db, 'parking_applications', application.id), {
        status: 'cancelled',
        updatedAt: new Date()
      });
      
      setApplication(prev => prev ? { ...prev, status: 'cancelled' } : null);
      setShowCancelModal(false);
      alert('신청이 취소되었습니다.');
    } catch (error) {
      console.error('신청 취소 중 오류:', error);
      alert('신청 취소 중 오류가 발생했습니다.');
    } finally {
      setCancelling(false);
    }
  };

  const handleEdit = () => {
    if (!application) return;
    navigate('/parking/application', { 
      state: { 
        editMode: true, 
        application,
        currentPeriod
      } 
    });
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="text-lg text-gray-600">로딩 중...</div>
        </div>
      </ClientLayout>
    );
  }

  if (!application) {
    return (
      <ClientLayout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">신청 내역을 찾을 수 없습니다.</p>
              <button
                onClick={() => navigate('/parking')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                돌아가기
              </button>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="w-[760px] mx-auto relative pt-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
                신청 내역 확인
              </div>
              <div className={`px-4 py-1.5 rounded-[5px] inline-flex items-center gap-2.5 ${
                application.status === 'pending' ? 'bg-[#f2f6fb]' :
                application.status === 'approved' ? 'bg-green-50' :
                application.status === 'rejected' ? 'bg-red-50' :
                'bg-gray-50'
              }`}>
                <div className="w-6 h-6 relative">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 7V3a4 4 0 118 0v4m-8 0H6a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" 
                      stroke={
                        application.status === 'pending' ? '#42506e' :
                        application.status === 'approved' ? '#16a34a' :
                        application.status === 'rejected' ? '#dc2626' :
                        '#6b7280'
                      } 
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className={`text-lg font-medium ${
                  application.status === 'pending' ? 'text-[#42506e]' :
                  application.status === 'approved' ? 'text-green-700' :
                  application.status === 'rejected' ? 'text-red-700' :
                  'text-gray-700'
                }`}>현재 상태</span>
                <span className={`text-lg font-bold ${
                  application.status === 'pending' ? 'text-[#1b89e5]' :
                  application.status === 'approved' ? 'text-green-600' :
                  application.status === 'rejected' ? 'text-red-600' :
                  'text-gray-600'
                }`}>{getStatusText(application.status)}</span>
              </div>
            </div>
            <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
            <div className="mt-8 flex items-center justify-between">
              <div className="text-[#1e2c49] text-2xl font-bold font-['Pretendard'] leading-9">
                주차장 정기신청 내역
              </div>
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          {/* 신청자 정보 */}
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                신청자 정보
              </h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <p className="text-[#6b7684] text-lg font-medium mb-2">성명</p>
                  <p className="text-[#1e2c49] text-[19px] font-semibold">{application.name}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-lg font-medium mb-2">소속</p>
                  <p className="text-[#1e2c49] text-[19px] font-semibold">{application.organization}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-lg font-medium mb-2">이메일</p>
                  <p className="text-[#1e2c49] text-[19px] font-semibold">{application.email}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-lg font-medium mb-2">연락처</p>
                  <p className="text-[#1e2c49] text-[19px] font-semibold">{application.phoneNumber}</p>
                </div>
              </div>
            </div>

            {/* 신청 내용 */}
            <div className="p-8 border-t border-[#e5e8eb]">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                신청 내용
              </h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <p className="text-[#6b7684] text-lg font-medium mb-2">신청 유형</p>
                  <p className="text-[#1e2c49] text-[19px] font-semibold">{getApplicationTypeText(application.applicationType)}</p>
                </div>
                <div>
                  <p className="text-[#6b7684] text-lg font-medium mb-2">신청 기간</p>
                  <p className="text-[#1e2c49] text-[19px] font-semibold">{application.period}개월</p>
                </div>
              </div>
            </div>

            {/* 추가 요청사항 */}
            {application.additionalRequest && (
              <div className="p-8 border-t border-[#e5e8eb]">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                  추가 요청사항
                </h2>
                <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
                <p className="text-[#1e2c49] text-[19px] font-semibold whitespace-pre-wrap">
                  {application.additionalRequest}
                </p>
              </div>
            )}

            {/* 약관 동의 */}
            <div className="p-8 border-t border-[#e5e8eb]">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                약관 동의
              </h2>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
              <div className="flex items-center gap-3">
                <svg 
                  className="w-6 h-6 text-green-500" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                    clipRule="evenodd" 
                  />
                </svg>
                <div>
                  <p className="text-[#1e2c49] text-[19px] font-semibold">이용약관에 동의했습니다</p>
                  <p className="text-[#6b7684] text-base mt-1">
                    동의일: {application.createdAt?.toDate?.()?.toLocaleDateString() || '알 수 없음'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 신청 일시 */}
          <div className="mt-8">
            <div className="flex justify-between text-[#6b7684] text-lg font-medium">
              <div>
                <p className="mb-2">신청일시</p>
                <p className="text-[#1e2c49]">
                  {application.createdAt?.toDate?.()?.toLocaleString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) || '알 수 없음'}
                </p>
              </div>
              <div>
                <p className="mb-2">마지막 수정</p>
                <p className="text-[#1e2c49]">
                  {application.updatedAt?.toDate?.()?.toLocaleString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) || '알 수 없음'}
                </p>
              </div>
            </div>
          </div>

          {/* 안내 메시지 */}
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 relative mt-1">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" 
                      stroke="#1b89e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 8V12L16 14" stroke="#1b89e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <p className="text-[#1e2c49] text-lg font-semibold mb-2">안내사항</p>
                  <p className="text-[#6b7684] text-base leading-relaxed">
                    {application.status === 'pending' && 
                      '신청이 접수되었습니다. 관리자 검토 후 추첨이 진행될 예정입니다.'
                    }
                    {application.status === 'approved' && 
                      '신청이 승인되었습니다. 추첨 결과를 기다려 주세요.'
                    }
                    {application.status === 'rejected' && 
                      '신청이 거절되었습니다. 사유는 별도 연락드릴 예정입니다.'
                    }
                    {application.status === 'cancelled' && 
                      '신청이 취소되었습니다. 새로운 신청을 하실 수 있습니다.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 하단 여백 - 고정 버튼을 위한 공간 확보 */}
          <div className="h-32"></div>

          {/* 액션 버튼 */}
          <div className="fixed bottom-0 left-0 w-full h-24 bg-white border-t border-gray-200">
            <div className="max-w-[1600px] mx-auto px-4 h-full flex items-center justify-end gap-4">
              <button
                onClick={() => navigate('/parking')}
                className="w-44 h-16 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <div className="text-center text-gray-700 text-lg font-bold font-['Pretendard'] leading-9">
                  목록으로 돌아가기
                </div>
              </button>
              
              {canModifyOrCancel() && (application.status === 'pending' || application.status === 'rejected') && (
                <button
                  onClick={handleEdit}
                  className="w-44 h-16 bg-green-600 rounded-sm hover:bg-green-700 transition-colors"
                >
                  <div className="text-center text-white text-lg font-bold font-['Pretendard'] leading-9">
                    신청 내용 수정
                  </div>
                </button>
              )}

              {canModifyOrCancel() && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-44 h-16 rounded-sm border border-[#ed5a5a] text-[#ed5a5a] hover:bg-red-50 transition-colors"
                >
                  <div className="text-center text-[#ed5a5a] text-lg font-bold font-['Pretendard'] leading-9">
                    신청 취소
                  </div>
                </button>
              )}

              {!canModifyOrCancel() && application.status !== 'cancelled' && (
                <div className="text-center text-gray-500 text-sm">
                  신청 기간이 아니거나 이전 차수의 신청이므로<br />
                  수정 및 취소가 불가능합니다.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 취소 확인 모달 */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">신청 취소 확인</h3>
              <p className="text-gray-600 mb-6">
                정말로 주차장 정기신청을 취소하시겠습니까?<br />
                취소된 신청은 되돌릴 수 없습니다.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelling}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {cancelling ? '처리 중...' : '신청 취소'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ClientLayout>
  );
};

export default ParkingDetailPage; 